import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchBarInputBox, { Suggestion, SuggestionType } from './components/SearchBarInputBox';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/user.selectors';
import { getAmpliSearchEventPayload, mapDesignPreference } from '../../utils/ampli';
import useRouter from '../../utils/useRouter';
import { SearchSubmitted } from '../../ampli';
import services from '../../services';
import { useNewDesign } from '../../state/DesignPreferenceContextProvider';
import { getRecentSearches } from '../../utils/recentSearches';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import { isClientNetworkError } from '../../utils/isClientNetworkError';
import useSearchQuery from '../utils/useSearchQuery';
import isCoursePage from '../../utils/isCoursePage';

interface SearchBarProps {
  closeDrawer?: () => void;
}

export type SearchCategory = 'Library' | 'Professional Development';

const SearchBar: React.FC<SearchBarProps> = ({ closeDrawer = () => {} }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [category, setCategory] = useState<SearchCategory>('Library');
  const [searchSuggestions, setSearchSuggestions] = useState<Suggestion[]>([]);
  const user = useSelector(selectUser);
  const { pushSearchQueryToRouter } = useSearchQuery();
  const { match } = useRouter();
  const { prefersNewDesign, shouldShowDesignPreferenceSwitch } = useNewDesign();

  useEffect(() => {
    if (isCoursePage(match.location.pathname)) {
      setCategory('Professional Development');
    } else {
      setCategory('Library');
    }
  }, [match.location.pathname]);

  useEffect(() => {
    const maxNumberOfSuggestions = 8;
    const timer = setTimeout(() => {
      if (!inputValue) {
        const recentSearches = user ? getRecentSearches(user.id) : [];
        const recentSearchesSuggestions = recentSearches
          .filter(search => search.term)
          .map((search) => ({
            label: search.term as string,
            value: search.term as string,
            type: SuggestionType.RecentSearch,
          }))
          .slice(0, maxNumberOfSuggestions);
        setSearchSuggestions(recentSearchesSuggestions);
        return;
      } else {
        services.mindItem.getSearchSuggestions(inputValue)
          .then(searchSuggestionsResponseData => {
            const suggestions = searchSuggestionsResponseData.map((suggestion) => ({
              label: suggestion.text,
              value: suggestion.text,
              type: SuggestionType.SearchSuggestion,
            })).slice(0, maxNumberOfSuggestions);
            setSearchSuggestions(suggestions);
          })
          .catch(error => { 
            if (isClientNetworkError(error.message)) notifyBugsnagHandledError(error);
          });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue, user]);

  useEffect(() => {
    const searchTerm = match.location.query && match.location.query['term'];
    setInputValue(searchTerm || '');
  }, [match.location.query]);

  const handleOnSearch = (term: string) => {
    if (user) {
      const searchPayload= getAmpliSearchEventPayload({
        query: match.location.query,
        term,
        appSection: category === 'Library' ? 'library' : 'courses',
      });

      if (searchPayload) { 
        const event = new SearchSubmitted(searchPayload as any);  
        if (shouldShowDesignPreferenceSwitch) {
          event.event_properties['ui'] = mapDesignPreference(prefersNewDesign);
        }        
        services.track.ampliEventTrack({ event });
      }
    }

    closeDrawer();

    pushSearchQueryToRouter({ term }, category);
  };

  return (
    <SearchBarInputBox
      suggestions={searchSuggestions}
      placeholder={t('Search by topic, lesson or lesson plan')}
      onSearch={handleOnSearch}
      inputValue={inputValue}
      setInputValue={setInputValue}
      category={category}
      setCategory={category => setCategory(category)}
    />
  );
};

export default SearchBar;
